import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  ChevronRight,
  CheckCircle,
} from 'lucide-react';
import featureService from '../../../core/services/featureService';
import { useSetup } from '../../../core/contexts/SetupContext';

const PREAUTH_SHELL =
  'qawaem-preauth-route flex min-h-screen w-full flex-col items-center overflow-x-hidden bg-gradient-to-br from-slate-900 via-blue-750 to-slate-900 px-4 py-8 sm:py-12';

const QAWAEM_FAVICON_SRC = encodeURI(
  `${process.env.PUBLIC_URL ?? ''}/qawaem/favicon.png`
);

const CARD_OUTER =
  'relative w-full overflow-hidden rounded-3xl border border-white/20 bg-white/10 shadow-xl shadow-black/25 backdrop-blur-lg';

const WelcomeLoginPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.shared.welcomeLoginPage.${key}`, opts);
  const navigate = useNavigate();
  const { loginUser, loading, isAuthenticated } = useSetup();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      setLoginSuccess(true);
      setTimeout(() => {
        navigate(featureService.getDefaultLandingRoute(), { replace: true });
      }, 800);
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = p('err_username_required');
    }
    if (!formData.password) {
      newErrors.password = p('err_password_required');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setErrors({});
      const result = await loginUser(formData);
      if (result) {
        setLoginSuccess(true);
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ submit: error.message || p('err_invalid_credentials') });
    }
  };

  if (loginSuccess) {
    return (
      <div className={PREAUTH_SHELL}>
        <div className={`mx-auto w-full max-w-md p-8 sm:p-10 ${CARD_OUTER}`}>
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-500/20 blur-3xl" aria-hidden />
          <div className="relative text-center">
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-400/35 bg-emerald-500/15"
            >
              <CheckCircle className="h-9 w-9 text-emerald-300" strokeWidth={2.2} aria-hidden />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-2xl font-bold text-emerald-200 sm:text-3xl"
            >
              {p('login_success_title')}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-3 text-white/65"
            >
              {p('redirect_dashboard')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25 }}
              className="mx-auto mt-8 h-11 w-11 rounded-full border-2 border-white/25 border-t-emerald-400 animate-spin"
              aria-hidden
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-8 rounded-xl border border-emerald-400/25 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100"
            >
              {p('setup_complete')}
            </motion.p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={PREAUTH_SHELL}>
      <div className={`mx-auto w-full max-w-lg p-6 sm:p-10 ${CARD_OUTER}`}>
        <div
          className="pointer-events-none absolute -left-14 -bottom-14 h-48 w-48 rounded-full bg-blue-500/15 blur-3xl"
          aria-hidden
        />
        <div className="relative">
          <div className="mb-8 text-center">
            <motion.img
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22 }}
              src={QAWAEM_FAVICON_SRC}
              alt="Qawaem"
              className="mx-auto mb-5 h-14 w-14 object-contain drop-shadow-[0_8px_24px_rgb(15_23_42/0.55)] sm:h-16 sm:w-16"
              draggable={false}
            />
            <h1 className="text-2xl font-bold text-white sm:text-3xl">{p('welcome_title')}</h1>
            <p className="mt-2 text-sm text-white/60 sm:text-base">
              {p('welcome_subtitle')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {errors.submit && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-2 rounded-xl border border-red-500/35 bg-red-500/10 px-4 py-3 text-sm text-red-100"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-300" aria-hidden />
                <span>{errors.submit}</span>
              </motion.div>
            )}

            <div>
              <label htmlFor="username" className="mb-2 block text-sm font-medium text-white/70">
                {p('username_label')}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35">
                  <User className="h-5 w-5" aria-hidden />
                </span>
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder={p('username_placeholder')}
                  autoComplete="username"
                  autoFocus
                  required
                  className="w-full rounded-xl border border-white/20 bg-white/5 py-3.5 pl-12 pr-4 text-white placeholder:text-white/35 focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </div>
              {errors.username && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-300">
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.username}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="mb-2 block text-sm font-medium text-white/70">
                {p('password_label')}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35">
                  <Lock className="h-5 w-5" aria-hidden />
                </span>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={p('password_placeholder')}
                  autoComplete="current-password"
                  required
                  className="w-full rounded-xl border border-white/20 bg-white/5 py-3.5 pl-12 pr-12 text-white placeholder:text-white/35 focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-white/45 transition-colors hover:text-white"
                  aria-label={showPassword ? p('hide_password') : p('show_password')}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" aria-hidden /> : <Eye className="h-5 w-5" aria-hidden />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-300">
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.password}
                </p>
              )}
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-blue-300/90 transition-colors hover:text-blue-200"
                >
                  {p('forgot_password')}
                </Link>
              </div>
            </div>

            <div className="flex justify-center pt-2">
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={loading ? {} : { scale: 1.03 }}
                whileTap={loading ? {} : { scale: 0.97 }}
                className="inline-flex min-w-[200px] items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-blue-500/25 disabled:cursor-not-allowed disabled:opacity-55"
              >
                {loading ? (
                  <>
                    <span className="h-5 w-5 shrink-0 rounded-full border-2 border-white/25 border-t-white animate-spin" />
                    {p('signing_in')}
                  </>
                ) : (
                  <>
                    {p('sign_in')}
                    <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
                  </>
                )}
              </motion.button>
            </div>
          </form>

          <p className="mt-8 border-t border-white/10 pt-6 text-center text-xs text-white/45 sm:text-sm">
            {p('footer_hint')}
            <span className="mt-2 block font-medium text-white/55">{p('footer_tagline')}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeLoginPage;
